<script setup lang="ts">
import { computed, ref, shallowRef, useTemplateRef, watch } from 'vue'
import type { Component } from 'vue'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  Layers,
  Loader2,
  RefreshCw,
  Server,
  Terminal,
  WifiOff
} from '@lucide/vue'
import { RrButton, RrEmpty, RrTag, message } from '@xjlv2/ui'
import type { LoginResponse } from '@xjlv2/api-client'
import { loadModuleComponent, remoteNameFor } from '../../composables/useModuleFederation'
import type {
  ModuleMenuItem,
  ModuleNotificationPayload,
  ModuleShellBridge,
  ModuleShellRegistration,
  ShellNotificationTarget
} from '../../types'

const props = defineProps<{
  menu: ModuleMenuItem
  session: LoginResponse
  target?: ShellNotificationTarget
}>()

const emit = defineEmits<{
  notify: [payload: ModuleNotificationPayload]
  'open-target': [target: ShellNotificationTarget]
  'register-module': [registration: ModuleShellRegistration]
  'session-expired': []
}>()

const remoteComponent = shallowRef<Component | null>(null)
const loading = shallowRef(false)
const error = shallowRef('')
const retryCount = ref(0)
const showDetails = ref(false)
const copied = ref(false)
const remoteInstance = useTemplateRef<{ refresh?: () => void | Promise<void> }>('remoteInstance')

const remoteName = computed(() => remoteNameFor(props.menu))
const remoteLoadKey = computed(() =>
  [remoteName.value, props.menu.remoteEntry, props.menu.exposedModule, retryCount.value].join(':')
)
const remoteComponentKey = computed(() =>
  [props.session.tenantId, props.menu.moduleCode, props.menu.remoteEntry, props.menu.exposedModule].join(':')
)

const isDevEnv = computed(() => {
  return typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    props.menu.remoteEntry.includes('localhost') ||
    props.menu.remoteEntry.includes('127.0.0.1')
  )
})

const isNetworkError = computed(() => {
  const msg = error.value.toLowerCase()
  return (
    msg.includes('fetch') ||
    msg.includes('network') ||
    msg.includes('load') ||
    msg.includes('failed to load') ||
    msg.includes('connection')
  )
})

const diagnosticInfo = computed(() => {
  return JSON.stringify(
    {
      moduleCode: props.menu.moduleCode,
      moduleName: props.menu.moduleName,
      menuCode: props.menu.code,
      menuName: props.menu.name,
      remoteName: remoteName.value,
      remoteEntry: props.menu.remoteEntry,
      exposedModule: props.menu.exposedModule,
      tenantId: props.session.tenantId,
      username: props.session.username,
      error: error.value,
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : ''
    },
    null,
    2
  )
})

const shellBridge = computed<ModuleShellBridge>(() => ({
  notify: (payload) => notifyShell(payload),
  open: (target) => emit('open-target', target),
  register: (registration) => registerModule(registration)
}))

async function doLoad() {
  if (!props.menu.remoteEntry || !props.menu.exposedModule) {
    error.value = '微前端配置不完整：缺少 remoteEntry 或 exposedModule 声明'
    loading.value = false
    return
  }

  loading.value = true
  error.value = ''
  try {
    remoteComponent.value = await loadModuleComponent(props.menu)
  } catch (err) {
    error.value = err instanceof Error ? err.message : '模块微前端加载失败'
    remoteComponent.value = null
  } finally {
    loading.value = false
  }
}

watch(
  remoteLoadKey,
  () => {
    void doLoad()
  },
  { immediate: true }
)

watch(
  () => props.session,
  () => {
    void remoteInstance.value?.refresh?.()
  },
  { deep: true }
)

function handleRetry() {
  retryCount.value++
}

function refresh() {
  return remoteInstance.value?.refresh?.()
}

function openRemoteEntry() {
  if (props.menu.remoteEntry) {
    window.open(props.menu.remoteEntry, '_blank', 'noopener,noreferrer')
  }
}

async function copyDiagnostics() {
  try {
    await navigator.clipboard.writeText(diagnosticInfo.value)
    copied.value = true
    message.success('诊断信息已复制到剪贴板')
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch {
    message.warning('复制失败，请手动选择复制')
  }
}

function notifyShell(payload: ModuleNotificationPayload) {
  emit('notify', {
    moduleCode: props.menu.moduleCode,
    moduleName: props.menu.moduleName,
    sender: props.menu.moduleName,
    target: {
      moduleCode: props.menu.moduleCode,
      menuCode: props.menu.code,
      path: props.menu.path
    },
    ...payload
  })
}

function openTarget(target: ShellNotificationTarget) {
  emit('open-target', target)
}

function registerModule(registration: ModuleShellRegistration) {
  emit('register-module', {
    ...registration,
    moduleCode: props.menu.moduleCode,
    moduleName: props.menu.moduleName,
    sender: registration.sender ?? props.menu.moduleName,
    target: {
      moduleCode: props.menu.moduleCode,
      menuCode: props.menu.code,
      path: props.menu.path,
      ...registration.target
    }
  })
}

defineExpose({
  refresh
})
</script>

<template>
  <section class="mfe-module-host">
    <!-- Mount ready modules without waiting for loading-view animation frames. -->
      <!-- 1. 加载中状态 (Skeleton & Connecting Loader) -->
      <div v-if="loading" key="loading" class="mfe-state-card mfe-loading-state">
        <div class="mfe-progress-bar">
          <div class="mfe-progress-indicator" />
        </div>

        <header class="mfe-state-header">
          <div class="mfe-meta-info">
            <div class="mfe-icon-wrapper is-loading">
              <Loader2 class="mfe-spin-icon" :size="20" />
            </div>
            <div>
              <div class="mfe-title-row">
                <h2 class="mfe-title">{{ menu.name }}</h2>
                <RrTag tone="primary" dot label="加载中" />
                <RrTag v-if="menu.permission" tone="neutral" :label="menu.permission" />
              </div>
              <p class="mfe-subtitle">
                <span>{{ remoteName }}</span>
                <span class="mfe-sep">/</span>
                <code>{{ menu.exposedModule }}</code>
              </p>
            </div>
          </div>
        </header>

        <!-- 骨架屏视图：中后台通用布局骨架 -->
        <div class="mfe-skeleton-container" aria-busy="true" aria-live="polite">
          <!-- 筛选栏骨架 -->
          <div class="mfe-skeleton-bar">
            <div class="mfe-skeleton-item is-input" />
            <div class="mfe-skeleton-item is-input is-short" />
            <div class="mfe-skeleton-item is-input is-short" />
            <div class="mfe-skeleton-spacer" />
            <div class="mfe-skeleton-item is-btn" />
            <div class="mfe-skeleton-item is-btn is-primary" />
          </div>

          <!-- 指标卡片骨架 -->
          <div class="mfe-skeleton-metrics">
            <div v-for="i in 4" :key="i" class="mfe-skeleton-metric-card">
              <div class="mfe-skeleton-item is-label" />
              <div class="mfe-skeleton-item is-value" />
              <div class="mfe-skeleton-item is-sub" />
            </div>
          </div>

          <!-- 数据表格骨架 -->
          <div class="mfe-skeleton-table">
            <div class="mfe-skeleton-thead">
              <div class="mfe-skeleton-item is-th is-checkbox" />
              <div class="mfe-skeleton-item is-th" style="width: 25%" />
              <div class="mfe-skeleton-item is-th" style="width: 20%" />
              <div class="mfe-skeleton-item is-th" style="width: 20%" />
              <div class="mfe-skeleton-item is-th" style="width: 15%" />
              <div class="mfe-skeleton-item is-th is-action" />
            </div>
            <div v-for="row in 4" :key="row" class="mfe-skeleton-tr">
              <div class="mfe-skeleton-item is-td is-checkbox" />
              <div class="mfe-skeleton-item is-td" style="width: 25%" />
              <div class="mfe-skeleton-item is-td" style="width: 20%" />
              <div class="mfe-skeleton-item is-td" style="width: 20%" />
              <div class="mfe-skeleton-item is-td" style="width: 15%" />
              <div class="mfe-skeleton-item is-td is-action" />
            </div>
          </div>
        </div>
      </div>

      <!-- 2. 加载失败状态 (Error Diagnostic Hero) -->
      <div v-else-if="error" key="error" class="mfe-state-card mfe-error-state">
        <header class="mfe-state-header is-error">
          <div class="mfe-meta-info">
            <div class="mfe-icon-wrapper is-error">
              <WifiOff v-if="isNetworkError" :size="22" />
              <AlertTriangle v-else :size="22" />
            </div>
            <div>
              <div class="mfe-title-row">
                <h2 class="mfe-title">{{ menu.name }} 加载失败</h2>
                <RrTag tone="danger" label="模块异常" />
                <RrTag v-if="menu.moduleCode" tone="neutral" :label="menu.moduleCode" />
              </div>
              <p class="mfe-subtitle">
                <span>{{ remoteName }}</span>
                <span class="mfe-sep">·</span>
                <code>{{ menu.exposedModule }}</code>
              </p>
            </div>
          </div>

          <div class="mfe-actions-row">
            <RrButton tone="primary" @click="handleRetry">
              <template #icon><RefreshCw :size="14" /></template>
              重新加载
            </RrButton>
            <RrButton v-if="menu.remoteEntry" @click="openRemoteEntry">
              <template #icon><ExternalLink :size="14" /></template>
              查看入口文件
            </RrButton>
          </div>
        </header>

        <div class="mfe-error-body">
          <div class="mfe-error-banner">
            <div class="mfe-error-message">
              <strong>错误描述：</strong>
              <span>{{ error }}</span>
            </div>

            <!-- 本地开发环境智能排查提示 -->
            <div v-if="isDevEnv" class="mfe-dev-tip">
              <div class="mfe-dev-tip-title">
                <Terminal :size="14" />
                <span>开发者排查提示</span>
              </div>
              <p>检测到微前端服务可能未启动。请确认对应前端子项目正在运行：</p>
              <div class="mfe-command-box">
                <code>pnpm --filter @xjlv2/{{ remoteName.replace(/_/g, '-') }} dev</code>
              </div>
              <p class="mfe-dev-tip-desc">
                或检查 <code>vite.config.ts</code> 模块联邦端口映射与 <code>remoteEntry</code> 配置是否一致。
              </p>
            </div>
          </div>

          <!-- 可折叠技术诊断信息 -->
          <div class="mfe-diagnostics-section">
            <button
              class="mfe-diagnostics-toggle"
              type="button"
              @click="showDetails = !showDetails"
            >
              <span>{{ showDetails ? '收起技术诊断详情' : '展开技术诊断详情' }}</span>
              <ChevronUp v-if="showDetails" :size="14" />
              <ChevronDown v-else :size="14" />
            </button>

            <div v-if="showDetails" class="mfe-diagnostics-content">
              <div class="mfe-diagnostics-bar">
                <span>技术上下文</span>
                <button
                  class="mfe-copy-btn"
                  type="button"
                  @click="copyDiagnostics"
                >
                  <Check v-if="copied" :size="13" class="is-success" />
                  <Copy v-else :size="13" />
                  <span>{{ copied ? '已复制' : '复制诊断 JSON' }}</span>
                </button>
              </div>
              <pre class="mfe-code-block"><code>{{ diagnosticInfo }}</code></pre>
            </div>
          </div>
        </div>
      </div>

      <!-- 3. 微前端入口未配置 (Empty State) -->
      <div
        v-else-if="!remoteComponent && !menu.remoteEntry"
        key="empty"
        class="mfe-state-card mfe-empty-state"
      >
        <RrEmpty
          title="微前端模块未配置"
          description="该菜单尚未指定远程微前端入口 (remoteEntry) 或导出模块 (exposedModule)。"
        >
          <template #image>
            <Layers :size="48" style="color: var(--rr-color-text-muted);" />
          </template>
        </RrEmpty>
      </div>

      <!-- 4. 正常挂载成功状态 (Mounted Remote Component) -->
      <div v-else-if="remoteComponent" :key="remoteComponentKey" class="mfe-remote-wrapper">
        <component
          ref="remoteInstance"
          :is="remoteComponent"
          :menu="menu"
          :session="session"
          :shell="shellBridge"
          :target="target"
          @notify="notifyShell"
          @open-target="openTarget"
          @session-expired="emit('session-expired')"
        />
      </div>
  </section>
</template>

<style scoped>
.mfe-module-host {
  width: 100%;
  min-height: calc(100vh - 120px);
  position: relative;
}

/* State Card Container */
.mfe-state-card {
  position: relative;
  background: var(--rr-color-surface);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-lg, 12px);
  padding: 24px;
  overflow: hidden;
  box-shadow: var(--rr-shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.05));
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Top Progress Bar Animation */
.mfe-progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--rr-color-primary-soft, #ebe7fc);
  overflow: hidden;
}

.mfe-progress-indicator {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 40%;
  background: var(--rr-color-primary, #5a46e0);
  border-radius: 2px;
  animation: mfe-progress-anim 1.5s ease-in-out infinite;
}

@keyframes mfe-progress-anim {
  0% {
    left: -40%;
    width: 30%;
  }
  50% {
    left: 40%;
    width: 50%;
  }
  100% {
    left: 100%;
    width: 30%;
  }
}

/* Header */
.mfe-state-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--rr-color-border-subtle, var(--rr-color-border));
}

.mfe-meta-info {
  display: flex;
  align-items: center;
  gap: 14px;
}

.mfe-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: var(--rr-radius-md, 8px);
  background: var(--rr-color-primary-soft, #ebe7fc);
  color: var(--rr-color-primary, #5a46e0);
  flex-shrink: 0;
}

.mfe-icon-wrapper.is-error {
  background: var(--rr-color-danger-soft, #fef3f2);
  color: var(--rr-color-danger, #d92d20);
}

.mfe-spin-icon {
  animation: mfe-spin 1s linear infinite;
}

@keyframes mfe-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.mfe-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.mfe-title {
  margin: 0;
  font-size: var(--rr-font-size-lg, 18px);
  font-weight: 600;
  color: var(--rr-color-text-strong, #1f2329);
  line-height: 1.3;
}

.mfe-subtitle {
  margin: 4px 0 0 0;
  font-size: var(--rr-font-size-xs, 12px);
  color: var(--rr-color-text-muted, #8f959e);
  display: flex;
  align-items: center;
  gap: 6px;
}

.mfe-subtitle code {
  font-family: var(--rr-font-mono, monospace);
  color: var(--rr-color-text, #333);
  background: var(--rr-color-canvas, #f5f6f7);
  padding: 1px 6px;
  border-radius: 4px;
}

.mfe-sep {
  color: var(--rr-color-text-muted, #ccc);
}

.mfe-actions-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Skeleton Styles */
.mfe-skeleton-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.mfe-skeleton-item {
  background: var(--rr-color-canvas, #f2f3f5);
  border-radius: var(--rr-radius-sm, 6px);
  position: relative;
  overflow: hidden;
}

.mfe-skeleton-item::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translateX(-100%);
  background-image: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0,
    rgba(255, 255, 255, 0.4) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: mfe-shimmer 1.8s infinite;
}

@keyframes mfe-shimmer {
  100% {
    transform: translateX(100%);
  }
}

/* Skeleton Bar */
.mfe-skeleton-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: var(--rr-color-surface, #fff);
  border: 1px solid var(--rr-color-border-subtle, var(--rr-color-border));
  border-radius: var(--rr-radius-md, 8px);
}

.mfe-skeleton-item.is-input {
  height: 32px;
  width: 180px;
}

.mfe-skeleton-item.is-input.is-short {
  width: 120px;
}

.mfe-skeleton-spacer {
  flex: 1;
}

.mfe-skeleton-item.is-btn {
  height: 32px;
  width: 72px;
}

.mfe-skeleton-item.is-btn.is-primary {
  width: 88px;
  background: var(--rr-color-primary-soft, #ebe7fc);
}

/* Skeleton Metrics */
.mfe-skeleton-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.mfe-skeleton-metric-card {
  padding: 16px;
  background: var(--rr-color-surface, #fff);
  border: 1px solid var(--rr-color-border-subtle, var(--rr-color-border));
  border-radius: var(--rr-radius-md, 8px);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mfe-skeleton-item.is-label {
  height: 14px;
  width: 60%;
}

.mfe-skeleton-item.is-value {
  height: 28px;
  width: 45%;
}

.mfe-skeleton-item.is-sub {
  height: 12px;
  width: 80%;
}

/* Skeleton Table */
.mfe-skeleton-table {
  border: 1px solid var(--rr-color-border-subtle, var(--rr-color-border));
  border-radius: var(--rr-radius-md, 8px);
  overflow: hidden;
}

.mfe-skeleton-thead,
.mfe-skeleton-tr {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
}

.mfe-skeleton-thead {
  background: var(--rr-color-canvas, #f8f9fa);
  border-bottom: 1px solid var(--rr-color-border-subtle, var(--rr-color-border));
}

.mfe-skeleton-tr {
  background: var(--rr-color-surface, #fff);
  border-bottom: 1px solid var(--rr-color-border-subtle, var(--rr-color-border));
}

.mfe-skeleton-tr:last-child {
  border-bottom: none;
}

.mfe-skeleton-item.is-th {
  height: 14px;
}

.mfe-skeleton-item.is-td {
  height: 18px;
}

.mfe-skeleton-item.is-checkbox {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.mfe-skeleton-item.is-action {
  width: 80px;
  margin-left: auto;
  flex-shrink: 0;
}

/* Error Body */
.mfe-error-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mfe-error-banner {
  background: var(--rr-color-danger-soft, #fef3f2);
  border: 1px solid var(--rr-color-danger-border, #fee4e2);
  border-radius: var(--rr-radius-md, 8px);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mfe-error-message {
  color: var(--rr-color-danger, #d92d20);
  font-size: var(--rr-font-size-sm, 14px);
  line-height: 1.5;
}

.mfe-dev-tip {
  background: var(--rr-color-surface, #fff);
  border: 1px solid var(--rr-color-danger-border, #fee4e2);
  border-radius: var(--rr-radius-sm, 6px);
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: var(--rr-font-size-xs, 12px);
  color: var(--rr-color-text, #333);
}

.mfe-dev-tip-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: var(--rr-color-text-strong, #1f2329);
}

.mfe-command-box {
  background: var(--rr-color-canvas, #1e1e1e);
  color: #fff;
  padding: 8px 12px;
  border-radius: 4px;
  overflow-x: auto;
}

.mfe-command-box code {
  font-family: var(--rr-font-mono, monospace);
  font-size: 12px;
}

.mfe-dev-tip-desc {
  margin: 0;
  color: var(--rr-color-text-muted, #8f959e);
}

/* Technical Diagnostics */
.mfe-diagnostics-section {
  border: 1px solid var(--rr-color-border-subtle, var(--rr-color-border));
  border-radius: var(--rr-radius-md, 8px);
  overflow: hidden;
}

.mfe-diagnostics-toggle {
  width: 100%;
  padding: 10px 14px;
  background: var(--rr-color-canvas, #f8f9fa);
  border: none;
  font-size: var(--rr-font-size-xs, 12px);
  color: var(--rr-color-text-muted, #666);
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: background 0.15s ease;
}

.mfe-diagnostics-toggle:hover {
  background: var(--rr-color-surface-hover, #f0f1f2);
  color: var(--rr-color-text-strong, #111);
}

.mfe-diagnostics-content {
  padding: 12px 14px;
  background: var(--rr-color-surface, #fff);
  border-top: 1px solid var(--rr-color-border-subtle, var(--rr-color-border));
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mfe-diagnostics-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--rr-font-size-xs, 12px);
  color: var(--rr-color-text-muted, #888);
}

.mfe-copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  background: var(--rr-color-canvas, #f2f3f5);
  border: 1px solid var(--rr-color-border, #e5e6eb);
  border-radius: 4px;
  font-size: 11px;
  color: var(--rr-color-text, #333);
  cursor: pointer;
  transition: all 0.15s ease;
}

.mfe-copy-btn:hover {
  background: var(--rr-color-surface-hover, #e8eaec);
  color: var(--rr-color-primary, #5a46e0);
}

.mfe-copy-btn .is-success {
  color: var(--rr-color-success, #129f54);
}

.mfe-code-block {
  margin: 0;
  padding: 10px 12px;
  background: var(--rr-color-canvas, #f8f9fa);
  border-radius: 4px;
  font-family: var(--rr-font-mono, monospace);
  font-size: 11px;
  line-height: 1.4;
  color: var(--rr-color-text, #333);
  max-height: 200px;
  overflow: auto;
}

/* Empty State */
.mfe-empty-state {
  min-height: 360px;
  justify-content: center;
  align-items: center;
}

/* Loaded Remote Wrapper */
.mfe-remote-wrapper {
  width: 100%;
  height: 100%;
}

</style>
